/** biome-ignore-all lint/suspicious/noExplicitAny: <explanation> */

import { useState } from 'react'
import { Controller } from 'react-hook-form'
import { TrashIcon } from '@/assets/icons/trash-icon'
import { EquivalencesSelect } from '@/components/inputs/equivalences-select'
import { TextViewOrEdit } from '@/components/inputs/text-view-or-edit'
import { Show } from '@/components/show'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { TableCell, TableRow } from '@/components/ui/table'
import { COURSE_STATUS } from '@/constants/course-status'
import { REQUEST_STATUS } from '@/constants/request-status'
import { cn } from '@/lib/utils'
import type { DesiredEducationCourse } from '@/types/education/desired-education-course'
import type { PreviousEducationCourse } from '@/types/education/previous-education-course'
import type { StudentRequestedCoursesFormMethods } from '@/types/forms/student-requested-courses-form-methods'
import { useCoursesEquivalencesRow } from './hooks/use-courses-equivalences-row'

interface CoursesEquivalencesTableRowProps {
  formMethods?: StudentRequestedCoursesFormMethods
  curriculumCourses?: DesiredEducationCourse[]
  fieldKey: 'courseEquivalences' | 'automaticCourseEquivalences'
  index: number
  editablePreviousCourses: boolean
  editableDesiredCourses: boolean
  availableCollegeCourses?: DesiredEducationCourse[]
  availablePreviousCourses?: PreviousEducationCourse[]
  onReview: boolean
  showStatusColumn?: boolean
  variant: 'student' | 'manager'
  canCreateNewPreviousCourse?: boolean
  requestStatus?: string
}

export const CoursesEquivalencesTableRow = ({
  formMethods,
  index,
  editablePreviousCourses,
  editableDesiredCourses,
  availableCollegeCourses,
  availablePreviousCourses,
  onReview,
  fieldKey,
  canCreateNewPreviousCourse,
  showStatusColumn = true,
  requestStatus
}: CoursesEquivalencesTableRowProps) => {
  const [olyAdaptationStatusAllowed, setOnlyAdaptationStatusAllowed] =
    useState(false)

  const handleSelectOptions = (index: number) => {
    const hasBackup = getBackupValue(index) !== undefined
    const hasValue = fieldHasValue(index)

    if (!hasValue && !hasBackup && !editablePreviousCourses) {
      setOnlyAdaptationStatusAllowed(true)
    } else {
      setOnlyAdaptationStatusAllowed(false)
    }
  }

  const {
    actions: {
      handleDesiredCourseSelect,
      getCollegeCourseOptions,
      getPreviousCoursesOptions,
      handlePreviousCourseValue,
      handleWorkload,
      handleFinalNumericGrade,
      handlePeriod,
      handleStatusChange,
      getBackupValue,
      fieldHasValue,
      handleRemoveField
    }
  } = useCoursesEquivalencesRow(
    index,
    availableCollegeCourses,
    availablePreviousCourses,
    formMethods,
    fieldKey
  )

  const canShowAdaptationModal =
    requestStatus !== REQUEST_STATUS.UNDER_REVIEW.id

  return (
    <TableRow className="bg-background hover:bg-background">
      <TableCell className="py-0">
        <Controller
          name={`${fieldKey}.${index}.desiredCourse`}
          control={formMethods?.control}
          render={({ field }) => (
            <EquivalencesSelect
              formControl={formMethods?.control}
              courseType="desiredCourse"
              options={getCollegeCourseOptions()}
              selectedValue={{
                id: field.value?.id,
                label: field.value?.name ?? '',
                data: field.value
              }}
              error={
                formMethods?.errors?.[fieldKey]?.[index]?.desiredCourse
                  ?.courseId
              }
              canCreateNew={false}
              canSelect={editableDesiredCourses}
              onChange={(value) => handleDesiredCourseSelect(value, index)}
            />
          )}
        />
      </TableCell>
      <TableCell className="py-0">
        <Controller
          name={`${fieldKey}.${index}.desiredCourse`}
          control={formMethods?.control}
          render={({ field }) => (
            <TextViewOrEdit
              value={
                field.value?.workload != null ? `${field.value.workload}h` : ''
              }
              editable={false}
            />
          )}
        />
      </TableCell>
      <TableCell className="py-0">
        <div className="h-10 w-[2px] bg-ring/80"></div>
      </TableCell>
      <TableCell className={'py-0'}>
        <Controller
          name={`${fieldKey}.${index}`}
          control={formMethods?.control}
          render={({ field }) => {
            const isAdaptation =
              field.value?.equivalenceStatus?.id === COURSE_STATUS.ADAPTATION.id

            return (
              <EquivalencesSelect
                formControl={formMethods?.control}
                courseType="previousCourse"
                error={
                  formMethods?.errors?.[fieldKey]?.[index]?.previousCourse?.name
                }
                options={getPreviousCoursesOptions()}
                selectedValue={{
                  id: field.value?.previousCourse?.id,
                  label: field.value?.previousCourse?.name || '',
                  data: field.value?.previousCourse
                }}
                canCreateNew={
                  canCreateNewPreviousCourse !== undefined
                    ? canCreateNewPreviousCourse
                    : true
                }
                canSelect={editablePreviousCourses && !isAdaptation}
                showAdaptationModal={isAdaptation && canShowAdaptationModal}
                onChange={(value) => handlePreviousCourseValue(value, index)}
              />
            )
          }}
        />
      </TableCell>
      <TableCell className="py-0 w-20">
        <Controller
          name={`${fieldKey}.${index}` as any}
          control={formMethods?.control}
          render={({ field }) => {
            const isAdaptation =
              field.value?.equivalenceStatus?.id === COURSE_STATUS.ADAPTATION.id
            const workloadSuffix =
              editablePreviousCourses || isAdaptation ? '' : 'h'
            return (
              <TextViewOrEdit
                error={
                  formMethods?.errors?.[fieldKey]?.[index]?.previousCourse
                    ?.workload
                }
                onChange={(value) => {
                  handleWorkload(value, index)
                }}
                value={`${field.value?.previousCourse?.workload ?? ''}${workloadSuffix}`}
                clearRegex={/[^0-9]/g}
                editable={
                  editablePreviousCourses
                  && fieldKey !== 'automaticCourseEquivalences'
                  && field.value?.equivalenceStatus?.id
                    !== COURSE_STATUS.ADAPTATION.id
                }
                placeholder="0"
              />
            )
          }}
        />
      </TableCell>
      <TableCell className="py-0 w-20">
        <Controller
          name={`${fieldKey}.${index}`}
          control={formMethods?.control}
          render={({ field }) => (
            <TextViewOrEdit
              error={
                formMethods?.errors?.[fieldKey]?.[index]?.previousCourse
                  ?.finalNumericGrade
              }
              onChange={(value) => {
                handleFinalNumericGrade(value, index)
              }}
              clearRegex={/[^0-9.]/g}
              value={field.value?.previousCourse?.finalNumericGrade || ''}
              editable={
                editablePreviousCourses
                && fieldKey !== 'automaticCourseEquivalences'
                && field.value?.equivalenceStatus?.id
                  !== COURSE_STATUS.ADAPTATION.id
              }
              placeholder="0.00"
            />
          )}
        />
      </TableCell>
      <TableCell className="py-0 w-35">
        <Controller
          name={`${fieldKey}.${index}` as any}
          control={formMethods?.control}
          render={({ field }) => (
            <TextViewOrEdit
              error={
                formMethods?.errors?.[fieldKey]?.[index]?.previousCourse?.period
              }
              onChange={(value) => {
                handlePeriod(value, index)
              }}
              value={field.value?.previousCourse?.period || ''}
              editable={
                editablePreviousCourses
                && fieldKey !== 'automaticCourseEquivalences'
                && field.value?.equivalenceStatus?.id
                  !== COURSE_STATUS.ADAPTATION.id
              }
              placeholder="Período"
            />
          )}
        />
      </TableCell>
      <Show when={showStatusColumn}>
        <TableCell className="py-0 pe-0 w-52">
          <Controller
            name={`${fieldKey}.${index}`}
            control={formMethods?.control}
            render={({ field }) => {
              return (
                <Select
                  disabled={!onReview}
                  onValueChange={(value) =>
                    handleStatusChange(value, field, index)
                  }
                  value={field.value?.equivalenceStatus?.id || ''}
                  onOpenChange={(value: boolean) => {
                    if (value) handleSelectOptions(index)
                  }}
                >
                  <SelectTrigger
                    className={cn('w-52', {
                      'border border-destructive':
                        formMethods?.errors?.[fieldKey]?.[index]
                          ?.equivalenceStatus
                    })}
                  >
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {!olyAdaptationStatusAllowed && (
                      <>
                        <SelectItem value={COURSE_STATUS.GRANT.id}>
                          {COURSE_STATUS.GRANT.label}
                        </SelectItem>
                        <SelectItem value={COURSE_STATUS.NOT_GRANT.id}>
                          {COURSE_STATUS.NOT_GRANT.label}
                        </SelectItem>
                      </>
                    )}

                    <SelectItem value={COURSE_STATUS.ADAPTATION.id}>
                      {COURSE_STATUS.ADAPTATION.label}
                    </SelectItem>
                  </SelectContent>
                </Select>
              )
            }}
          />
        </TableCell>
      </Show>
      <TableCell className="relative w-0 px-0 mx-0">
        <Show
          when={
            editablePreviousCourses
            && fieldKey !== 'automaticCourseEquivalences'
          }
        >
          <button
            type="button"
            onClick={handleRemoveField}
            className="absolute text-destructive top-[15px] ms-[6px] cursor-pointer"
          >
            <TrashIcon />
          </button>
        </Show>
      </TableCell>
    </TableRow>
  )
}
